import React from "react"
import { useDispatch } from "react-redux"
import { Formik, Form } from "formik"
import * as Yup from "yup"
import dayjs from "dayjs"
import {
	Dialog,
	styled,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Stack,
	TextField,
	InputLabel,
	Autocomplete
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import { COLORS, getTimezoneDate } from "../../utils"
import { downloadSupplierItemRecipesCsv } from "../../actions/ProductionSubstitutions/ProductionSubstitutions"
import SupplierItemRecipesDetailRadio, { SUPPLIER_ITEM_RECIPES_DETAILS } from "./SupplierItemRecipesDetailRadio"

const formId = "supplierItemRecipesExport"

const StyledDialog = styled(Dialog)({
	"& .MuiPaper-root": {
		width: 500,
		padding: 10,
	},
})

const sx = {
	title: {
		pb: 0,
		fontSize: 16,
		color: COLORS.BLACK_500,
		fontWeight: 400,
		lineHeight: 1.375,
	},
	dialogContent: {
		mt: 5,
		mb: 1,
		p: 4,
	},
	dialogActions: {
		display: "flex",
		justifyContent: "space-between",
		pl: 4,
		pr: 4
	},
	error: {
		color: "red",
		fontSize: 12,
		mt: 1
	}
}

const validationSchema = Yup.object().shape({
	supplierItem: Yup.object().nullable().required("Veuillez sélectionner un article"),
	startDate: Yup.number().required("Veuillez sélectionner une date de début"),
	endDate: Yup.number()
		.required("Veuillez sélectionner une date de fin")
		.min(Yup.ref("startDate"), "La date de fin doit être après la date de début")
})

const SupplierItemRecipesExportDialog = ({ open, onClose, supplierItems }) => {
	const dispatch = useDispatch()

	const supplierItemsOptions = supplierItems.map(supplierItem => ({
		label: supplierItem.objectId + " - " + supplierItem.name,
		value: supplierItem.objectId,
		name: supplierItem.name
	}))

	const handleSubmit = async (values) => {
		await dispatch(downloadSupplierItemRecipesCsv({
			supplierItem: values.supplierItem,
			// getTimezoneDate gives the UTC midnight of the picked day
			startDate: dayjs.utc(values.startDate).format("YYYY-MM-DD"),
			endDate: dayjs.utc(values.endDate).format("YYYY-MM-DD"),
			detail: values.detail
		}))
		onClose()
	}

	if (!open) return null

	return (
		<StyledDialog
			open={open}
			onClose={onClose}
			aria-labelledby="supplier-item-recipes-export-title"
		>
			<DialogTitle id="supplier-item-recipes-export-title" sx={sx.title}>
				Sélectionnez l’article et la période de production. Le fichier liste, jour par jour, les recettes qui contiennent l’article et leurs volumes par marque.
			</DialogTitle>
			<DialogContent sx={sx.dialogContent}>
				<Formik
					onSubmit={handleSubmit}
					initialValues={{
						supplierItem: null,
						startDate: getTimezoneDate(new Date()),
						endDate: getTimezoneDate(new Date()),
						detail: SUPPLIER_ITEM_RECIPES_DETAILS.PRODUCTION_DAY
					}}
					validateOnBlur={false}
					validateOnChange={false}
					validationSchema={validationSchema}
				>
					{({ values, errors, setFieldValue }) => (
						<Form id={formId}>
							<Stack gap={5}>
								<Stack>
									<Autocomplete
										id="supplierItem"
										options={supplierItemsOptions}
										value={values.supplierItem}
										getOptionLabel={(option) => option.label}
										isOptionEqualToValue={(option, value) => option.value === value?.value}
										onChange={(_, value) => setFieldValue("supplierItem", value)}
										renderInput={(params) => (
											<TextField
												{...params}
												variant="standard"
												label="Article fournisseur"
												error={!!errors.supplierItem}
											/>
										)}
									/>
									{errors.supplierItem && <Stack sx={sx.error}>{errors.supplierItem}</Stack>}
								</Stack>
								<Stack>
									<InputLabel>Date de début de production</InputLabel>
									<DatePicker
										showToolbar={false}
										value={values.startDate}
										inputFormat="DD/MM/YY"
										renderInput={(params) => <TextField {...params} variant="standard" error={!!errors.startDate} InputLabelProps={{ shrink: true }} />}
										onChange={(date) => setFieldValue("startDate", getTimezoneDate(date))}
									/>
									{errors.startDate && <Stack sx={sx.error}>{errors.startDate}</Stack>}
								</Stack>
								<Stack>
									<InputLabel>Date de fin de production</InputLabel>
									<DatePicker
										showToolbar={false}
										value={values.endDate}
										inputFormat="DD/MM/YY"
										renderInput={(params) => <TextField {...params} variant="standard" error={!!errors.endDate} InputLabelProps={{ shrink: true }} />}
										onChange={(date) => setFieldValue("endDate", getTimezoneDate(date))}
									/>
									{errors.endDate && <Stack sx={sx.error}>{errors.endDate}</Stack>}
								</Stack>
								<SupplierItemRecipesDetailRadio value={values.detail} onChange={(detail) => setFieldValue("detail", detail)} />
							</Stack>
						</Form>
					)}
				</Formik>
			</DialogContent>
			<DialogActions sx={sx.dialogActions}>
				<Button onClick={onClose} color="primary">
					Annuler
				</Button>
				<Button
					color="primary"
					variant="contained"
					type="submit"
					form={formId}
				>
					Exporter
				</Button>
			</DialogActions>
		</StyledDialog>
	)
}

export default SupplierItemRecipesExportDialog
